import { isRateLimited } from '@dawn/core/utils';
import { RetryableErrorPage } from '@dawn/ui';
import type { UseQueryResult } from '@tanstack/react-query';

import { rateLimitRetryDelay } from '@/lib/http-errors';

type FailedQuery = Pick<
  UseQueryResult<unknown>,
  'error' | 'failureReason' | 'failureCount' | 'isFetching' | 'refetch'
>;

/**
 * A bootstrap query that failed with anything but an invalid tenant (TDEV-1107).
 * A 429 counts down to TanStack's own retry while it is pending, then offers
 * Retry once retries run out; any other error offers Retry at once. Server
 * text is never shown.
 */
export function QueryErrorPage({ query }: { query: FailedQuery }) {
  const { error, failureReason, failureCount, isFetching, refetch } = query;
  const handleRetry = isFetching ? undefined : () => void refetch();

  if (!isRateLimited(failureReason ?? error)) {
    return (
      <RetryableErrorPage
        title="Something went wrong"
        body="We couldn't load the dashboard. Please try again."
        onRetry={handleRetry}
      />
    );
  }

  // failureCount is 1-based; TanStack called retryDelay with the 0-based attempt.
  // No failureReason while fetching = a manual retry is in flight.
  let countdown: number | undefined;
  if (isFetching) {
    countdown = failureReason
      ? Math.ceil(rateLimitRetryDelay(failureCount - 1, failureReason) / 1000)
      : 0;
  }

  return (
    <RetryableErrorPage
      key={failureCount}
      title="Too many requests"
      body="Your workspace is sending more requests than we can handle right now."
      countdown={countdown}
      onRetry={handleRetry}
    />
  );
}
